// @flow
import * as React from 'react';
import { Link } from 'react-router';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';

type Props = {
  params: {
    userId: number,
    organisationId: number
  },
  currentUser: Object,
  currentOrganisation: Object,
  addUserToOrganisation: (u: Object, oid: any, s: any) => Promise<*>,
  removeUserFromOrganisation: (
    u: Object,
    oid: number,
    uoid: number
  ) => Promise<*>,
  getAllocatedUserOrganisations: (uid: number) => Promise<*>,
  getUnallocatedUserOrganisations: (uid: number) => Promise<*>
};

type State = {
  newOrganisationId: ?number,
  newOrganisationName?: string,
  showNewOrganisation: boolean,
  filterValue: string,
  isSaving?: boolean,
  errorMsg?: string,
  successMsg?: string
};

const ControlHeader = props => (
  <Row className="row-header tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>
    <Col sm={5} className="table-header" />
    <Col sm={3} className="table-header">
      <Button
        bsStyle="default"
        type="submit"
        className="relative-padding pull-right"
        onClick={props.assignButtonClick}
      >
        Assign
      </Button>
    </Col>
  </Row>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="User Customer Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
        disabled={props.isSaving}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const { item, removeFromOrganisation } = props;

  return (
    <Row key={item.organisationId}>
      <Col sm={9} className="table-entry">
        <Link to={`/customers/${item.organisationId}`}>
          {item.organisationName}
        </Link>
      </Col>
      <Col sm={3} className="table-entry text-center">
        {item.userOrganisationId && (
          <Button
            bsStyle="link"
            onClick={e => removeFromOrganisation(item)}
            aria-label={`Remove ${item.organisationName}`}
          >
            Remove
          </Button>
        )}
      </Col>
    </Row>
  );
};

export default class UserCustomers extends React.Component<Props, State> {
  fuzzyFilterFactory: any;
  timeoutId: any;
  screenReaderContent: any;
  updateFilterValue: any;
  static displayName = 'UserCustomers';

  constructor(props: Props) {
    super(props);

    this.state = {
      newOrganisationId: undefined,
      showNewOrganisation: false,
      filterValue: ''
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    const {
      currentUser: { id: userId },
      getUnallocatedUserOrganisations,
      getAllocatedUserOrganisations
    } = this.props;
    getAllocatedUserOrganisations(userId);
    getUnallocatedUserOrganisations(userId);
  }

  assignButtonClick = () => {
    this.setState({ showNewOrganisation: !this.state.showNewOrganisation });
  };

  cancel = () => {
    this.setState({ showNewOrganisation: false, newOrganisationId: undefined });
  };

  selectOnChange = (newOrganisationId: number, newOrganisationName: string) => {
    this.setState({ newOrganisationId, newOrganisationName });
  };

  addToOrganisation = () => {
    if (this.state.newOrganisationId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const { newOrganisationId } = this.state;
      const {
        currentUser,
        currentUser: { id: userId },
        addUserToOrganisation,
        getAllocatedUserOrganisations,
        getUnallocatedUserOrganisations
      } = this.props;

      addUserToOrganisation(currentUser, newOrganisationId).then(
        response => {
          getAllocatedUserOrganisations(userId);
          getUnallocatedUserOrganisations(userId);
          this.success(response);
        },
        error => this.failure(error)
      );
    }
  };

  removeFromOrganisation = (item: Object) => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const {
      getAllocatedUserOrganisations,
      getUnallocatedUserOrganisations,
      removeUserFromOrganisation,
      currentUser,
      currentUser: { id: userId }
    } = this.props;

    removeUserFromOrganisation(
      currentUser,
      item.organisationId,
      item.userOrganisationId
    ).then(
      response => {
        getAllocatedUserOrganisations(userId);
        getUnallocatedUserOrganisations(userId);
        this.success(response);
      },
      error => this.failure(error)
    );
  };

  failure = (error: Object) => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  success = (response: Object) => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  render() {
    const {
      currentUser: {
        organisations: { allocated, unallocated }
      },
      params
    } = this.props;

    const LineItemWrapper = props => (
      <LineItem
        {...props}
        organisationId={params.organisationId}
        removeFromOrganisation={this.removeFromOrganisation}
        currentUser={this.props.currentUser}
      />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing accounts for {this.props.currentUser.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          assignButtonClick={this.assignButtonClick}
        />
        <Row>
          <Col sm={9} className="table-header">
            Customer Name
          </Col>
          <Col sm={3} className="table-header text-center">
            Actions
          </Col>
        </Row>
        <hr />

        {this.state.showNewOrganisation && (
          <AddNewRow
            items={unallocated}
            addItem={this.addToOrganisation}
            selectValue={this.state.newOrganisationId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
            isSaving={this.state.isSaving}
          />
        )}

        <PaginatedList
          filterValue={this.state.filterValue}
          component={LineItemWrapper}
          items={allocated}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          pluralName="sub-accounts"
          keys={['organisationId', 'organisationName']}
        />
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/users/UserCustomers.js